import React from "react";
import {Amplify} from "aws-amplify";
import {Outlet} from "react-router-dom";
import {Container, PermanentMenu} from "@bounce/shared/ui";
import {useAuth} from "hooks/useAuth";
import {getMenuItems} from "./get-menu-items";
import {LOCAL_STORAGE_CLIENT_ID_KEY} from "hooks/useAmplifyConfig";
import {useAuthContext} from "../contexts/AuthContext";
import {isInternalUser} from "../utils/userUtils";
import {useGetHasOfActionRequired} from "../api/requests/partner-level/numOfActionRequired";
import {useDocumentRequestsActionRequired} from "api/requests/partner-level/documentRequests";
import {usePartnerContext} from "../contexts/PartnerContext";
import PartnerInfoHeader from "./PartnerInfoHeader";
import PartnerSwitcherDropdown from "./PartnerSwitcherDropdown";
import {usePartnerSwitcher} from "./usePartnerSwitcher";

const MenuContent = () => {
  const {username, partnerCode, email, partnersGroup, setPartnerCode, setPartnersGroup, isMultiPartnerUser, session} =
    useAuthContext();
  const {isFpPartner, isLoading: isPartnerLoading, partnerName, partnerType} = usePartnerContext();
  const {handleSignOut} = useAuth();

  const isInternal = isInternalUser(username);
  const isMultiPartner = isInternal || isMultiPartnerUser;
  const isReadyForApiCalls = !!partnerCode && !!session?.tokens?.accessToken;

  const {
    switcherAnchorEl,
    showSwitcherDropdown,
    handleSwitcherClick,
    handleSwitcherClose,
    handlePartnerSelect,
    currentPartnerSymbol,
  } = usePartnerSwitcher({
    username,
    partnerCode,
    session,
    partnersGroup,
    setPartnerCode,
    setPartnersGroup,
    isInternal,
    isMultiPartnerUser,
  });

  const {data: hasActionRequired} = useGetHasOfActionRequired(
    {partnerCode: partnerCode},
    {enabled: isReadyForApiCalls}
  );
  const {data: documentRequestsActionRequired} = useDocumentRequestsActionRequired(
    {partnerCode},
    {enabled: isReadyForApiCalls}
  );

  const onSignOut = async () => {
    await handleSignOut();
    localStorage.removeItem(LOCAL_STORAGE_CLIENT_ID_KEY);
    sessionStorage.removeItem("partnerId");
    Amplify.configure({});
  };

  return (
    <Container row sx={{height: "100%", alignItems: "stretch", gap: 0}}>
      <PermanentMenu
        menuItems={getMenuItems(
          hasActionRequired,
          isInternal,
          documentRequestsActionRequired,
          isFpPartner,
          isPartnerLoading,
          isMultiPartner
        )}
        header={
          <PartnerInfoHeader
            partnerName={partnerName || ""}
            partnerSymbol={currentPartnerSymbol}
            email={email}
            partnerType={partnerType ?? undefined}
            firstPartyEnabled={isFpPartner}
            onSwitchClick={handleSwitcherClick}
          />
        }
        platform="partner"
      />

      <PartnerSwitcherDropdown
        partners={partnersGroup}
        currentPartnerCode={partnerCode || ""}
        currentPartnerName={partnerName || ""}
        currentPartnerType={partnerType || ""}
        currentPartnerSymbol={currentPartnerSymbol}
        email={email || ""}
        onSelect={handlePartnerSelect}
        onClose={handleSwitcherClose}
        onSignOut={onSignOut}
        anchorEl={switcherAnchorEl}
        open={showSwitcherDropdown}
      />

      <Outlet />
    </Container>
  );
};

const MenuLayout = () => {
  return <MenuContent />;
};

export default MenuLayout;
